import type { ScorerWaitList } from '../lib/scorer-wait-list';
import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';
import { SCORING_POLL_MS } from '../lib/shooting';

export interface CheckInPollLane {
  lane: number;
  shooterId: string | null;
  name: string | null;
  bib: string | null;
  status: 'on_lane' | 'shooting' | null;
}

export interface CheckInPollShooter {
  status: string;
}

/** The slice of the check-in payload the event-day counts and the lane list need. */
export interface CheckInPollState {
  shooters: CheckInPollShooter[];
  lanes: CheckInPollLane[];
  waiting: unknown[];
  scorer?: ScorerWaitList;
  refresh?: () => void;
}

/**
 * Live check-in for the counts strip and the lane list.
 * Admin only — the check-in route refuses everyone else, so callers pass enabled=false for them.
 */
export function useCheckInPoll(tournamentId: string | null, enabled: boolean, round: 1 | 2 = 1): CheckInPollState | null {
  const [state, setState] = useState<CheckInPollState | null>(null);

  const refresh = useRef<() => void>(() => {});
  useEffect(() => {
    if (!enabled || !tournamentId) {
      setState(null);
      return;
    }
    setState(null);
    let cancelled = false;
    let sequence = 0;
    let readsInFlight = 0;
    const load = async (background = false) => {
      if (background && readsInFlight > 0) return;
      const current = ++sequence;
      readsInFlight++;
      try {
        const body = await apiFetch<CheckInPollState>(`/api/admin/shooting/tournaments/${tournamentId}/${round === 2 ? 'caller?round=2' : 'check-in'}`);
        if (!cancelled && current === sequence) setState(body);
      } catch {
        // Keep the last good snapshot. The desk page reports its own load error.
      } finally {
        readsInFlight--;
      }
    };
    refresh.current = () => { void load(); };
    void load();
    const timer = window.setInterval(() => void load(true), SCORING_POLL_MS);
    return () => {
      cancelled = true;
      refresh.current = () => {};
      window.clearInterval(timer);
    };
  }, [enabled, tournamentId, round]);

  return state ? { ...state, refresh: () => refresh.current() } : null;
}
